import {Offer} from '@findhotel/sapi'
import {
  getIsSplitBookingOfferTheCheapest,
  getIsSplitBookingOfferYourChoice,
  getSplitBookingDetails,
  getSplitBookingPriceSummary
} from 'modules/sapiSearch/selectors'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {assoc, dissoc, mapObjIndexed} from 'ramda'

import {isSplitBookingPriceEligible} from '@vio/core/src/offer/business/isSplitBookingPriceEligible'
import {getNightlyPriceForAllRooms} from '@vio/core/src/price/business/price'
import {numberOfRooms} from '@vio/core/src/room/business/roomConfiguration'

import type {getSimilarHotelsPricingContext} from 'modules/sapiSearch/similarHotelsSelectors'

type PricingContext = ReturnType<typeof getSimilarHotelsPricingContext>
type PricedOffers = {price: number; offers: Offer[]}

const isValidPrice = (price: number) => Number.isFinite(price) && price > 0

const getRooms = (
  id: string,
  entity: HotelOfferEntity,
  context: PricingContext
) => {
  const isAnchor = id === context.anchorId
  const optimizeRooms = isAnchor
    ? context.search?.optimizeAnchorRooms
    : context.search?.optimizeRooms

  if (optimizeRooms && entity.roomsConfig) return entity.roomsConfig

  return context.search?.rooms
}

const getRegularOfferDisplayPrice = (
  offer: HotelOfferEntity['offers'][number] | undefined,
  rooms: string,
  context: PricingContext
): number | undefined => {
  if (!offer) return undefined

  const price = context.showTotalPrices
    ? offer.totalRate
    : getNightlyPriceForAllRooms(
        offer.nightlyRate,
        context.isMultiRoomBundlesSearch,
        numberOfRooms(rooms || '')
      )

  if (!isValidPrice(price)) return undefined

  return price
}

const getSplitBookingDisplayPrice = (
  entity: HotelOfferEntity,
  context: PricingContext
) => {
  const summary = getSplitBookingPriceSummary.resultFunc(entity)

  return context.showTotalPrices ? summary.sum : summary.average
}

const getCheapestHotelOffer = (
  entity: HotelOfferEntity,
  id: string,
  context: PricingContext
): PricedOffers | undefined => {
  const rooms = getRooms(id, entity, context)
  const candidates: PricedOffers[] = entity.offers.flatMap(offer => {
    const price = getRegularOfferDisplayPrice(offer, rooms, context)

    return price === undefined ? [] : [{price, offers: [offer]}]
  })
  const regularTotals = entity.offers
    .map(offer => offer.totalRate)
    .filter(isValidPrice)
  const cheapestRegularTotal = regularTotals.length
    ? Math.min(...regularTotals)
    : 0
  const bundle = entity.splitBooking
  const isEligibleBundle =
    bundle?.bundleId &&
    bundle.url &&
    bundle.offers?.length &&
    !bundle.inProgress &&
    isSplitBookingPriceEligible(bundle.totalRate, cheapestRegularTotal)

  if (isEligibleBundle) {
    const price = getSplitBookingDisplayPrice(entity, context)

    if (isValidPrice(price))
      candidates.push({price, offers: bundle.offers.map(part => part.offer)})
  }

  return candidates.sort((a, b) => a.price - b.price)[0]
}

/** Select the cheapest eligible offer while retaining its terms for links and comparisons. */
export const getSimilarHotelCheapestOffers = (
  offers: Record<string, HotelOfferEntity>,
  context: PricingContext
): Record<string, PricedOffers | undefined> =>
  mapObjIndexed(
    (entity, id) => getCheapestHotelOffer(entity, id, context),
    offers
  )

/**
 * Provide the prices used in ranking and on cards: the anchor's displayed primary price and each alternative's cheapest eligible price.
 */
export const getSimilarHotelDisplayPrices = (
  offers: Record<string, HotelOfferEntity>,
  context: ReturnType<typeof getSimilarHotelsPricingContext>,
  cheapest = getSimilarHotelCheapestOffers(offers, context)
): Record<string, number> => {
  const prices = Object.fromEntries(
    Object.entries(cheapest).flatMap(([id, offer]) =>
      offer ? [[id, offer.price]] : []
    )
  )
  const entity = offers[context.anchorId]

  if (!entity) return prices

  // The anchor keeps its displayed primary offer, even when a cheaper offer exists.
  const split = getSplitBookingDetails.resultFunc(
    entity,
    context.moduleState,
    context.search,
    getIsSplitBookingOfferTheCheapest.resultFunc(entity),
    getIsSplitBookingOfferYourChoice.resultFunc(entity),
    context.isFromGha,
    context.isFromGvr
  )
  const anchorPrice = split.hasPrimarySplitBookingOffer
    ? getSplitBookingDisplayPrice(entity, context)
    : getRegularOfferDisplayPrice(
        entity.offers[0],
        getRooms(context.anchorId, entity, context),
        context
      )

  return anchorPrice === undefined
    ? dissoc(context.anchorId, prices)
    : assoc(context.anchorId, anchorPrice, prices)
}
